/**
 * Feature Context - Manages feature flags throughout the application
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { FeatureFlags, DEFAULT_FEATURES } from '../config/features';

interface FeatureContextType {
  features: FeatureFlags;
  isFeatureEnabled: (feature: keyof FeatureFlags) => boolean;
  toggleFeature: (feature: keyof FeatureFlags) => void;
  enableFeature: (feature: keyof FeatureFlags) => void;
  disableFeature: (feature: keyof FeatureFlags) => void;
  resetFeatures: () => void;
}

const FeatureContext = createContext<FeatureContextType | undefined>(undefined);

interface FeatureProviderProps {
  children: ReactNode;
  initialFeatures?: Partial<FeatureFlags>;
}

export function FeatureProvider({ children, initialFeatures = {} }: FeatureProviderProps) {
  const [features, setFeatures] = useState<FeatureFlags>(() => {
    // Try to load from localStorage first
    try {
      const saved = localStorage.getItem('rogue-trader-features');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...DEFAULT_FEATURES, ...parsed, ...initialFeatures };
      }
    } catch (error) {
      console.warn('Failed to load feature flags from localStorage:', error);
    }

    return { ...DEFAULT_FEATURES, ...initialFeatures };
  });

  // Save to localStorage whenever features change
  useEffect(() => {
    try {
      localStorage.setItem('rogue-trader-features', JSON.stringify(features));
    } catch (error) {
      console.warn('Failed to save feature flags to localStorage:', error);
    }
  }, [features]);

  const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
    return features[feature];
  };

  const toggleFeature = (feature: keyof FeatureFlags) => {
    setFeatures((prev) => ({
      ...prev,
      [feature]: !prev[feature]
    }));
    console.log(`🎛️ Feature ${feature} ${features[feature] ? 'disabled' : 'enabled'}`);
  };

  const enableFeature = (feature: keyof FeatureFlags) => {
    setFeatures((prev) => ({
      ...prev,
      [feature]: true
    }));
    console.log(`✅ Feature ${feature} enabled`);
  };

  const disableFeature = (feature: keyof FeatureFlags) => {
    setFeatures((prev) => ({
      ...prev,
      [feature]: false
    }));
    console.log(`❌ Feature ${feature} disabled`);
  };

  const resetFeatures = () => {
    setFeatures(DEFAULT_FEATURES);
    console.log('🔄 Features reset to defaults');
  };

  const contextValue: FeatureContextType = {
    features,
    isFeatureEnabled,
    toggleFeature,
    enableFeature,
    disableFeature,
    resetFeatures
  };

  return <FeatureContext.Provider value={contextValue}>{children}</FeatureContext.Provider>;
}

export function useFeatures(): FeatureContextType {
  const context = useContext(FeatureContext);
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
}

// Convenience hook for checking individual features
export function useFeature(feature: keyof FeatureFlags): boolean {
  const { isFeatureEnabled } = useFeatures();
  return isFeatureEnabled(feature);
}
